import { useCallback, useState } from 'react'
import {
  faFileAlt as OrderDetailsIcon,
  faList as OrderCardsIcon,
} from '@fortawesome/free-solid-svg-icons'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Currency from 'components/ui/Currency'
import TSTable from 'components/Tanstack'

import { useTableUtils } from 'hooks/useTableUtils'
import {
  OrdersWestfieldConsDataTypes,
  OrdersWestfieldConsModalActionsTypes,
} from 'types/orders-westfieldcons'

export const useTablesOrders = ({
  dataTable,
}: {
  dataTable: OrdersWestfieldConsDataTypes[]
}) => {
  const {
    selectedModal,
    selectedModalSet,
    selectedRowData,
    selectedRowDataSet,
  } = useTableUtils<
    OrdersWestfieldConsDataTypes,
    OrdersWestfieldConsModalActionsTypes
  >()

  const handleActionModalClose = useCallback(() => {
    selectedRowDataSet(null)
    selectedModalSet(null)
  }, [selectedModalSet, selectedRowDataSet])

  const handleViewOderDetailModalOpen = useCallback(
    (rowData: OrdersWestfieldConsDataTypes) => {
      selectedRowDataSet(rowData)
      selectedModalSet('orders_details_preview')
    },
    [selectedRowDataSet, selectedModalSet]
  )

  const handleViewOderCardModalOpen = useCallback(
    (rowData: OrdersWestfieldConsDataTypes) => {
      selectedRowDataSet(rowData)
      selectedModalSet('orders_cards_preview')
    },
    [selectedRowDataSet, selectedModalSet]
  )

  const columnHelper = createColumnHelper<OrdersWestfieldConsDataTypes>()
  const columns = [
    columnHelper.accessor('orderId', {
      header: () => 'Order #',
      cell: (info) => {
        const orderId = info.getValue()

        return (
          <p className="flex flex-col">
            <span>{orderId}</span>
          </p>
        )
      },
    }),
    columnHelper.accessor('trackNumber', {
      header: () => 'Tracking Details',
      cell: ({ row }) => {
        const { original } = row
        const { trackNumber, trackingStatus } = original

        return (
          <>
            <p>{trackNumber ?? ''}</p>
            <p className="font-light">{trackingStatus}</p>
          </>
        )
      },
    }),

    columnHelper.accessor('orderDate', {
      header: () => 'Date',
      cell: (info) => {
        const dateString = info.getValue()
        if (!dateString) return '-'

        const [datePart, timePart] = dateString.split(' ')
        const [year, month, day] = datePart.split('-')

        return `${day}/${month}/${year} ${timePart}`
      },
    }),
    columnHelper.accessor('amount', {
      header: () => 'Amount',
      cell: ({ row }) => {
        const { original } = row
        const { amount } = original

        return (
          <p className="font-bold">
            {amount.toLocaleString(undefined, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}
            <Currency countryCode={'NZ'} />
          </p>
        )
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: () => '',
      // cell: (info) => info.getValue(),
      cell: ({ row }) => (
        <div className="flex gap-2 px-4">
          <TSTable.TableCellButton
            onClick={() => handleViewOderDetailModalOpen(row.original)}
            label="Details"
            icon={OrderDetailsIcon}
          />
          <TSTable.TableCellButton
            onClick={() => handleViewOderCardModalOpen(row.original)}
            label="Cards"
            icon={OrderCardsIcon}
          />
        </div>
      ),
    }),
  ] as ColumnDef<OrdersWestfieldConsDataTypes>[]

  return {
    selectedRowData,
    selectedModal,
    dataTable,
    columns,
    handleActionModalClose,
  }
}
